import { FC, ReactElement, useState, useLayoutEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  styled,
} from '@mui/material';

import sidebarSubItemArrow from '@assets/images/sidebar/sidebarSubItemArrow.svg';
import markAnyGrayLogo from '@assets/images/markAnyGrayLogo.svg';
import markAnyStaffLogo from '@assets/images/markAnyStaffLogo.svg';
import sidebarItemOneIcon from '@assets/images/sidebar/sidebarItemOne.svg';
import sidebarItemTwoIcon from '@assets/images/sidebar/sidebarItemTwo.svg';
import sidebarItemThreeIcon from '@assets/images/sidebar/sidebarItemThree.svg';
import sidebarItemFourIcon from '@assets/images/sidebar/sidebarItemFour.svg';
import sidebarItemFiveIcon from '@assets/images/sidebar/sidebarItemFive.svg';

import { ROUTES } from 'constants/routes';
import { isAdmin } from '@utils/getRole';

const StyledListItemButton = styled(ListItemButton)`
  position: relative;
  padding: 20px 30px;
  &::before {
    position: absolute;
    content: '';
    width: 100%;
    height: 1.5px;
    top: 0;
    left: 0;
  }
  &::after {
    position: absolute;
    content: '';
    width: 100%;
    height: 1.5px;
    bottom: 0;
    left: 0;
  }
`;

const StyledListItemText = styled(ListItemText)`
  font-size: 17px;
  line-height: 22px;
  font-weight: 400;
  margin: unset;
`;

const StyledListSubItemButton = styled(ListItemButton)`
  padding: 16px 30px 16px 34px;
`;

const StyledListSubItemText = styled(ListItemText)`
  font-size: 15px;
  line-height: 22px;
  font-weight: 400;
  margin: unset;
`;

const DashboardAside: FC = (): ReactElement => {
  const { fileId, employeeId, id } = useParams();
  const navigateTo = useNavigate();
  const location = useLocation();
  const [selectedIndex, setSelectedIndex] = useState(0);

  let roleBgColor: string = 'var(--navy-800)';
  let roleFeaturesFlag = {
    customerDeliveryFiles: false,
    externalDeliveryFiles: false,
    employeeManagement: false,
    operationHistory: false,
    system: false,
  };

  if (isAdmin) {
    roleBgColor = 'var(--navy-800)';
    roleFeaturesFlag = {
      customerDeliveryFiles: true,
      externalDeliveryFiles: true,
      employeeManagement: true,
      operationHistory: true,
      system: true,
    };
  } else {
    roleBgColor = 'var(--navy-600)';
    roleFeaturesFlag = {
      customerDeliveryFiles: true,
      externalDeliveryFiles: false,
      employeeManagement: false,
      operationHistory: false,
      system: false,
    };
  }

  const [menuItemIndex] = useState({
    'Customer Delivery Files': {
      index: 0,
      pathname: ROUTES.CUSTOMER_DELIVERY_FILES.ROOT,
    },
    'Customer Delivery Files Register': {
      index: 1,
      parent: 'Customer Delivery Files',
      pathname: ROUTES.CUSTOMER_DELIVERY_FILES.REGISTER_FILE.path,
    },
    'Customer Delivery Files Update': {
      index: 2,
      parent: 'Customer Delivery Files',
      pathname: `${ROUTES.CUSTOMER_DELIVERY_FILES.UPDATE_FILE.path}/${fileId}`,
    },
    'External Delivery Files': {
      index: 3,
      pathname: ROUTES.EXTERNAL_DELIVERY_FILES.ROOT,
    },

    'External Delivery Files Register': {
      index: 4,
      parent: 'External Delivery Files',
      pathname: ROUTES.EXTERNAL_DELIVERY_FILES.REGISTER_FILE.path,
    },
    'External Delivery Files Update': {
      index: 5,
      parent: 'External Delivery Files',
      pathname: `${ROUTES.EXTERNAL_DELIVERY_FILES.UPDATE_FILE.path}/${fileId}`,
    },
    'Employee Management': {
      index: 6,
      pathname: ROUTES.EMPLOYEE_MANAGEMENT.ROOT,
    },
    'Employee Management Update': {
      index: 7,
      parent: 'Employee Management',
      pathname: `${ROUTES.EMPLOYEE_MANAGEMENT.UPDATE_FILE.path}/${employeeId}`,
    },
    'Operation History': {
      index: 8,
      pathname: ROUTES.OPERATION_HISTORY.CUSTOMER_DELIVERY_DOWNLOAD_FILES.path,
    },
    'Customer Delivery Download Files': {
      index: 9,
      parent: 'Operating History',
      pathname: ROUTES.OPERATION_HISTORY.CUSTOMER_DELIVERY_DOWNLOAD_FILES.path,
    },
    'External Delivery Download Files': {
      index: 10,
      parent: 'Operating History',
      pathname: ROUTES.OPERATION_HISTORY.EXTERNAL_DELIVERY_DOWNLOAD_FILES.path,
    },
    'System':
    {
      index: 11,
      pathname: ROUTES.SYSTEM.PREFERENCES.path
    },
    'Preferences': {
      index: 12,
      parent: 'System',
      pathname: ROUTES.SYSTEM.PREFERENCES.path,
    },
    'Manager': {
      index: 13,
      parent: 'System',
      pathname: ROUTES.SYSTEM.MANAGER.path,
    },
    'Manager Update': {
      index: 14,
      parent: 'System',
      pathname: `${ROUTES.SYSTEM.MANAGER.path}/change-information/${id}`
    }
  })

  const isCustomerDeliveryFilesActive =
    selectedIndex === menuItemIndex['Customer Delivery Files']?.index ||
    selectedIndex ===
    menuItemIndex['Customer Delivery Files Register']?.index ||
    selectedIndex === menuItemIndex['Customer Delivery Files Update']?.index;

  const isExternalDeliveryFilesActive =
    selectedIndex === menuItemIndex['External Delivery Files']?.index ||
    selectedIndex ===
    menuItemIndex['External Delivery Files Register']?.index ||
    selectedIndex === menuItemIndex['External Delivery Files Update']?.index;

  const isEmployeeManagementActive =
    selectedIndex === menuItemIndex['Employee Management']?.index ||
    selectedIndex === menuItemIndex['Employee Management Update']?.index;

  const isManagerActive =
    selectedIndex === menuItemIndex['Manager'].index ||
    selectedIndex === menuItemIndex['Manager Update']?.index


  const handleListItemClick = (index: number, routePath: string) => {
    const pageNo = location?.state?.page
    const notEmpty = location?.state?.notEmpty
    setSelectedIndex(index);
    if ((pageNo && pageNo !== 1) || (notEmpty)) {
      return navigateTo(routePath, { state: { refresh: true } })
    }
    navigateTo(routePath);
  };

  const temp = (path: string) => {
    const routeLevels = path.split('/').slice(1);
    const routeRootLevel = routeLevels[0];
    const routeAction = routeLevels.slice(-2)[0];
    return { routeLevels, routeRootLevel, routeAction };
  };

  useLayoutEffect(() => {
    const { pathname } = window.location;
    const {
      routeRootLevel: windowRouteRootLevel,
      routeAction: windowRouteAction,
    } = temp(pathname);
    const isDynamicRoute = ['update', 'download', 'change-information']?.includes(windowRouteAction);
    const menuIndex =
      Object.values(menuItemIndex)?.find((menuItem) => {
        if (isDynamicRoute) {
          const {
            routeRootLevel: stateRouteRootLevel,
            routeAction: stateRouteAction,
          } = temp(menuItem?.pathname);

          return (
            windowRouteRootLevel === stateRouteRootLevel &&
            windowRouteAction === stateRouteAction
          );
        } else {
          return menuItem.pathname === pathname;
        }
      })?.index || 0;

    setSelectedIndex(menuIndex);
    // if (isDynamicRoute) {
    //   const menuLabel = Object.keys(menuItemIndex)[menuIndex];
    //   setMenuItemIndex((prevMenuItemIndex) => {
    //     return {
    //       ...prevMenuItemIndex,
    //       [menuLabel]: { ...prevMenuItemIndex[menuLabel], pathname },
    //     };
    //   });
    // }
  }, [location]);

  return (
    <aside
      style={{
        minWidth: '300px',
        maxWidth: '300px',
        backgroundColor: roleBgColor,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      <Collapse
        orientation="horizontal"
        collapsedSize={72}
        sx={{
          transition: 'all 500ms cubic-bezier(0.4, 1, 0.2, 1) 0ms !important',
          width: '100%',
          '&.MuiCollapse-root': {
            '& .MuiCollapse-wrapper': {
              '& .MuiCollapse-wrapperInner': {
                width: '100%',
              },
            },
          },
        }}
      >
        <List
          component="nav"
          aria-labelledby="nested-list-subheader"
          sx={{
            padding: '30px 0 0 0',
            width: '100%',
          }}
        >
          <StyledListItemButton
            selected={
              selectedIndex === menuItemIndex['Customer Delivery Files'].index
            }
            onClick={() => {
              handleListItemClick(
                menuItemIndex['Customer Delivery Files'].index,
                ROUTES.CUSTOMER_DELIVERY_FILES.ROOT,
              );
            }}
            sx={{
              background: isCustomerDeliveryFilesActive
                ? 'var(--sidebar-active-color) !important'
                : 'transparent',
              '&::before': {
                background: isCustomerDeliveryFilesActive
                  ? 'var(--sidebar-active-line-color) !important'
                  : 'transparent',
              },

              '&::after': {
                background: isCustomerDeliveryFilesActive
                  ? 'var(--sidebar-active-line-color) !important'
                  : 'transparent',
              },
            }}
          >
            <ListItemIcon>
              <img
                src={sidebarItemOneIcon}
                alt="Customer Delivery Files"
                width={24}
                height={24}
              />
            </ListItemIcon>
            <StyledListItemText
              primary="고객 전달용 대용량 파일"
              disableTypography
              sx={{
                color: isCustomerDeliveryFilesActive
                  ? 'var(--base-white)'
                  : 'var(--neutral-700)',
              }}
            />
          </StyledListItemButton>

          {roleFeaturesFlag.externalDeliveryFiles && (
            <StyledListItemButton
              selected={
                selectedIndex === menuItemIndex['External Delivery Files'].index
              }
              onClick={() => {
                handleListItemClick(
                  menuItemIndex['External Delivery Files'].index,
                  ROUTES.EXTERNAL_DELIVERY_FILES.ROOT,
                );
              }}
              sx={{
                background: isExternalDeliveryFilesActive
                  ? 'var(--sidebar-active-color) !important'
                  : 'transparent',
                '&::before': {
                  background: isExternalDeliveryFilesActive
                    ? 'var(--sidebar-active-line-color) !important'
                    : 'transparent',
                },

                '&::after': {
                  background: isExternalDeliveryFilesActive
                    ? 'var(--sidebar-active-line-color) !important'
                    : 'transparent',
                },
              }}
            >
              <ListItemIcon>
                <img
                  src={sidebarItemTwoIcon}
                  alt="External Delivery Files"
                  width={24}
                  height={24}
                />
              </ListItemIcon>
              <StyledListItemText
                primary="외부 링크용 대용량 파일"
                disableTypography
                sx={{
                  color: isExternalDeliveryFilesActive
                    ? 'var(--base-white)'
                    : 'var(--neutral-700)',
                }}
              />
            </StyledListItemButton>
          )}

          {roleFeaturesFlag.employeeManagement && (
            <StyledListItemButton
              selected={
                selectedIndex === menuItemIndex['Employee Management'].index
              }
              onClick={() => {
                handleListItemClick(
                  menuItemIndex['Employee Management'].index,
                  ROUTES.EMPLOYEE_MANAGEMENT.ROOT,
                );
              }}
              sx={{
                background: isEmployeeManagementActive
                  ? 'var(--sidebar-active-color) !important'
                  : 'transparent',
                '&::before': {
                  background: isEmployeeManagementActive
                    ? 'var(--sidebar-active-line-color) !important'
                    : 'transparent',
                },

                '&::after': {
                  background: isEmployeeManagementActive
                    ? 'var(--sidebar-active-line-color) !important'
                    : 'transparent',
                },
              }}
            >
              <ListItemIcon>
                <img
                  src={sidebarItemThreeIcon}
                  alt="Employee Management"
                  width={24}
                  height={24}
                />
              </ListItemIcon>
              <StyledListItemText
                primary="마크애니 직원 관리"
                disableTypography
                sx={{
                  color: isEmployeeManagementActive
                    ? 'var(--base-white)'
                    : 'var(--neutral-700)',
                }}
              />
            </StyledListItemButton>
          )}

          {roleFeaturesFlag.operationHistory && (
            <>
              <StyledListItemButton
                selected={
                  selectedIndex === menuItemIndex['Operation History'].index
                }
                onClick={() => {
                  handleListItemClick(
                    menuItemIndex['Operation History'].index,
                    ROUTES.OPERATION_HISTORY.CUSTOMER_DELIVERY_DOWNLOAD_FILES
                      .path,
                  );
                }}
                sx={{
                  background:
                    selectedIndex ===
                      menuItemIndex['Operation History'].index ||
                      selectedIndex ===
                      menuItemIndex['External Delivery Download Files'].index
                      ? 'var(--sidebar-active-color) !important'
                      : 'transparent',

                  '&::before': {
                    background:
                      selectedIndex ===
                        menuItemIndex['Operation History'].index ||
                        selectedIndex ===
                        menuItemIndex['External Delivery Download Files'].index
                        ? 'var(--sidebar-active-line-color) !important'
                        : 'transparent',
                  },

                  '&::after': {
                    background:
                      selectedIndex ===
                        menuItemIndex['Operation History'].index ||
                        selectedIndex ===
                        menuItemIndex['External Delivery Download Files'].index
                        ? 'var(--sidebar-active-line-color) !important'
                        : 'transparent',
                  },
                }}
              >
                <ListItemIcon>
                  <img
                    src={sidebarItemFourIcon}
                    alt="Operation History"
                    width={24}
                    height={24}
                  />
                </ListItemIcon>
                <StyledListItemText
                  primary="운영 이력"
                  disableTypography
                  sx={{
                    color:
                      selectedIndex ===
                        menuItemIndex['Operation History'].index ||
                        selectedIndex ===
                        menuItemIndex['External Delivery Download Files'].index
                        ? 'var(--base-white)'
                        : 'var(--neutral-700)',
                  }}
                />
              </StyledListItemButton>

              <Collapse
                in={
                  selectedIndex === menuItemIndex['Operation History'].index ||
                  selectedIndex ===
                  menuItemIndex['External Delivery Download Files'].index
                }
                timeout="auto"
                unmountOnExit
              >
                <List component="div" disablePadding>
                  <StyledListSubItemButton
                    onClick={() => {
                      handleListItemClick(
                        menuItemIndex['Operation History'].index,
                        ROUTES.OPERATION_HISTORY
                          .CUSTOMER_DELIVERY_DOWNLOAD_FILES.path,
                      );
                    }}
                  >
                    <ListItemIcon>
                      {selectedIndex ===
                        menuItemIndex['Operation History'].index && (
                          <img
                            src={sidebarSubItemArrow}
                            alt="sidebarSubItemArrow"
                            width={20}
                            height={20}
                          />
                        )}
                    </ListItemIcon>
                    <StyledListSubItemText
                      primary="고객 전달용 파일 다운로드"
                      disableTypography
                      sx={{
                        color:
                          selectedIndex ===
                            menuItemIndex['Operation History'].index
                            ? 'var(--base-white)'
                            : 'var(--neutral-800)',
                      }}
                    />
                  </StyledListSubItemButton>
                  <StyledListSubItemButton
                    onClick={() => {
                      handleListItemClick(
                        menuItemIndex['External Delivery Download Files'].index,
                        ROUTES.OPERATION_HISTORY
                          .EXTERNAL_DELIVERY_DOWNLOAD_FILES.path,
                      );
                    }}
                  >
                    <ListItemIcon>
                      {selectedIndex ===
                        menuItemIndex['External Delivery Download Files']
                          .index && (
                          <img
                            src={sidebarSubItemArrow}
                            alt="sidebarSubItemArrow"
                            width={20}
                            height={20}
                          />
                        )}
                    </ListItemIcon>
                    <StyledListSubItemText
                      primary="외부 전달용 파일 다운로드"
                      disableTypography
                      sx={{
                        color:
                          selectedIndex ===
                            menuItemIndex['External Delivery Download Files']
                              .index
                            ? 'var(--base-white)'
                            : 'var(--neutral-800)',
                      }}
                    />
                  </StyledListSubItemButton>
                </List>
              </Collapse>
            </>
          )}
          {roleFeaturesFlag.system && (
            <>
              <StyledListItemButton
                selected={selectedIndex === menuItemIndex['System'].index}
                onClick={() => {
                  handleListItemClick(
                    menuItemIndex['System'].index,
                    ROUTES.SYSTEM.PREFERENCES.path,
                  );
                }}
                sx={{
                  background:
                    selectedIndex === menuItemIndex['System'].index ||
                      isManagerActive
                      ? 'var(--sidebar-active-color) !important'
                      : 'transparent',
                  padding: '20px 30px',
                  '&::before': {
                    background:
                      selectedIndex === menuItemIndex['System'].index ||
                        isManagerActive
                        ? 'var(--sidebar-active-line-color) !important'
                        : 'transparent',
                  },

                  '&::after': {
                    background:
                      selectedIndex === menuItemIndex['System'].index ||
                        isManagerActive
                        ? 'var(--sidebar-active-line-color) !important'
                        : 'transparent',
                  },
                }}
              >
                <ListItemIcon>
                  <img
                    src={sidebarItemFiveIcon}
                    alt="System"
                    width={24}
                    height={24}
                  />
                </ListItemIcon>
                <StyledListItemText
                  primary="시스템"
                  disableTypography
                  sx={{
                    color:
                      selectedIndex === menuItemIndex['System'].index ||
                        isManagerActive
                        ? 'var(--base-white)'
                        : 'var(--neutral-700)',
                  }}
                />
              </StyledListItemButton>
              <Collapse
                in={
                  selectedIndex === menuItemIndex['System'].index ||
                  isManagerActive
                }
                timeout="auto"
                unmountOnExit
              >
                <List component="div" disablePadding>
                  <StyledListSubItemButton
                    onClick={() => {
                      handleListItemClick(
                        menuItemIndex['System'].index,
                        ROUTES.SYSTEM.PREFERENCES.path,
                      );
                    }}
                  >
                    <ListItemIcon>
                      {selectedIndex === menuItemIndex['System'].index && (
                        <img
                          src={sidebarSubItemArrow}
                          alt="sidebarSubItemArrow"
                          width={20}
                          height={20}
                        />
                      )}
                    </ListItemIcon>
                    <StyledListSubItemText
                      primary="환경설정"
                      disableTypography
                      sx={{
                        color:
                          selectedIndex === menuItemIndex['System'].index
                            ? 'var(--base-white)'
                            : 'var(--neutral-800)',
                      }}
                    />
                  </StyledListSubItemButton>

                  <StyledListSubItemButton
                    onClick={() => {
                      handleListItemClick(
                        menuItemIndex['Manager'].index,
                        ROUTES.SYSTEM.MANAGER.path,
                      );
                    }}
                  >
                    <ListItemIcon>
                      {isManagerActive && (
                        <img
                          src={sidebarSubItemArrow}
                          alt="sidebarSubItemArrow"
                          width={20}
                          height={20}
                        />
                      )}
                    </ListItemIcon>
                    <StyledListSubItemText
                      primary="관리자"
                      disableTypography
                      sx={{
                        color:
                          isManagerActive
                            ? 'var(--base-white)'
                            : 'var(--neutral-800)',
                      }}
                    />
                  </StyledListSubItemButton>
                </List>
              </Collapse>
            </>
          )}
        </List>
      </Collapse>
      <Box
        display="flex"
        justifyContent="center"
        paddingTop="18px"
        paddingBottom="18px"
      >
        {isAdmin && (
          <img
            src={markAnyGrayLogo}
            alt="MarkAny Logo"
            width={88}
            height={24}
          />
        )}
        {!isAdmin && (
          <img
            src={markAnyStaffLogo}
            alt="MarkAny Logo"
            width={88}
            height={24}
          />
        )}
      </Box>
    </aside>
  );
};

export default DashboardAside;
